import React, { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { userPanelApi } from 'api';
import { usePanel } from 'contexts/panelContext';
import { useGlobal } from 'contexts/globalContext';
import { SessionType } from 'types/Session';
import { QuestionnaireAnswer, SessionQuestionnaire } from 'types/SessionQuestionnaire';
import {
	CreateSessionQuestionnairePayload,
	UpdateSessionQuestionnairePayload,
} from 'types/payloads/SessionQuestionnairePayload';
import { Button } from 'primereact/button';
import { Box, BoxSection } from 'components/_new/Box';
import { Line } from 'components/_new/Line';
import { StepSlider } from 'components/_new/Slider';
import { SelectButtonGroupYesNo } from 'components/_new/SelectButtonGroup';
import { Icon } from 'components/_new/Icon';
import { MessageBox, MessageBoxRef } from 'components/_new/MessageBox';
import { useSessionsData } from '../Sessions';

export type MentorEvaluationFormObject = Required<
	Pick<
		QuestionnaireAnswer,
		| 'goalsSetting'
		| 'givingFeedback'
		| 'communicationSkils'
		| 'listeningSkills'
		| 'questioningSkills'
		| 'experienceUsage'
		| 'newKnowledge'
		| 'newKnowledgeUsability'
		| 'value'
	>
>;

interface MentorEvaluationModuleProps {
	pairId: number;
	sessionType: SessionType;
	questionnairesData?: SessionQuestionnaire[];
	refetchQuestionnaires?: any;
}

const MentorEvaluationModule: FC<MentorEvaluationModuleProps> = ({
	pairId,
	sessionType,
	questionnairesData,
	refetchQuestionnaires,
}) => {
	const { t } = useTranslation();
	const { currentProgramMembership } = usePanel();
	const { toastRef } = useGlobal();
	const { sessiosRefetch } = useSessionsData();
	const currentQuestionnaire = questionnairesData?.find((questionnaire) => questionnaire.forMentorOnly === true);
	const questionnaireAnswers = currentQuestionnaire?.questionnaireAnswers;

	const baseDefaultFormValues = {
		goalsSetting: (questionnaireAnswers?.goalsSetting as number) ?? 0,
		givingFeedback: (questionnaireAnswers?.givingFeedback as number) ?? 0,
		communicationSkils: (questionnaireAnswers?.communicationSkils as number) ?? 0,
		listeningSkills: (questionnaireAnswers?.listeningSkills as number) ?? 0,
		questioningSkills: (questionnaireAnswers?.questioningSkills as number) ?? 0,
	};

	const defaultValues: any = useMemo(
		() =>
			Object.assign(
				baseDefaultFormValues,
				sessionType === 'middle' && { experienceUsage: questionnaireAnswers?.experienceUsage as boolean },
				sessionType === 'last' && {
					newKnowledge: questionnaireAnswers?.newKnowledge as boolean,
					newKnowledgeUsability: (questionnaireAnswers?.newKnowledgeUsability as number) ?? 0,
					experienceUsage: questionnaireAnswers?.experienceUsage as boolean,
					value: (questionnaireAnswers?.value as number) ?? 0,
				}
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[questionnaireAnswers, sessionType]
	);

	const autosaveMessageRefs = useRef<Record<string, MessageBoxRef>>({});

	const { control, handleSubmit, getValues } = useForm<MentorEvaluationFormObject>({
		defaultValues,
	});

	const { mutate: createQuestionnaire, isLoading: isCreateSubmitting } = useMutation(
		({ data, showToast = true }: { data: CreateSessionQuestionnairePayload; showToast?: boolean }) =>
			userPanelApi.createQuestionnaire(+pairId, currentProgramMembership.id, data),
		{
			onSuccess: (data, variables) => {
				refetchQuestionnaires();
				sessiosRefetch();
				if (variables.showToast) {
					toastRef?.current?.show({
						severity: 'success',
						life: 3000,
						summary: t('misc.success'),
						detail: t('userPanel.sessions.successfullSessionEditTooltip'),
					});
				}
			},
		}
	);

	const { mutate: updateQuestionnaire, isLoading: isUpdateSubmitting } = useMutation(
		({ data, showToast = true }: { data: UpdateSessionQuestionnairePayload; showToast?: boolean }) =>
			userPanelApi.updateQuestionnaire(+pairId, currentProgramMembership.id, sessionType, data),
		{
			onSuccess: (data, variables) => {
				refetchQuestionnaires();
				sessiosRefetch();

				if (variables.showToast) {
					toastRef?.current?.show({
						severity: 'success',
						life: 3000,
						summary: t('misc.success'),
						detail: t('userPanel.sessions.successfullSessionEditTooltip'),
					});
				}
			},
		}
	);

	const autosave = (fieldName: string) => {
		const value = getValues(fieldName as any);
		const defaultValue = defaultValues[fieldName];

		if (value !== defaultValue) {
			if (currentQuestionnaire) {
				updateQuestionnaire({
					data: {
						id: currentQuestionnaire.id,
						questionnaireAnswers: { [fieldName]: value },
					},
					showToast: false,
				});
			} else {
				createQuestionnaire({
					data: {
						sessionType,
						forMentorOnly: true,
						questionnaireAnswers: { ...getValues(), [fieldName]: value },
					},
					showToast: false,
				});
			}

			autosaveMessageRefs.current[fieldName]?.show();
		}
	};

	const onSubmit = handleSubmit((data) => {
		if (!questionnairesData?.length || !currentQuestionnaire) {
			createQuestionnaire({
				data: {
					sessionType,
					forMentorOnly: true,
					questionnaireAnswers: data,
				},
				showToast: true,
			});
		} else {
			updateQuestionnaire({
				data: {
					id: currentQuestionnaire.id,
					questionnaireAnswers: data,
				},
				showToast: true,
			});
		}
	});

	return (
		<form className="flex flex-column row-gap-4" onSubmit={onSubmit}>
			{Object.entries(defaultValues).map(([k, v]) => {
				const title = t(`userPanel.sessions.mentorEvaluation.questionnaire.${k}`);
				switch (k) {
					case 'goalsSetting':
					case 'givingFeedback':
					case 'communicationSkils':
					case 'listeningSkills':
					case 'questioningSkills':
					case 'newKnowledgeUsability':
					case 'value':
						return (
							<Box key={k} variant="white-bordered">
								<Controller
									name={k as any}
									control={control}
									render={({ field: { ref, onChange, value } }) => (
										<BoxSection
											header={() => (
												<div className="flex flex-row gap-2 justify-content-start align-items-center">
													{title}
													{(!v || !value) && (
														<Icon name="circle" className="text-xs text-rose" />
													)}
												</div>
											)}
											headerClassName="pt-2 font-semibold heading-16-24 text-on-blue-grey"
											contentClassName="pb-2"
										>
											<MessageBox
												ref={(el: MessageBoxRef) => {
													if (el) {
														autosaveMessageRefs.current[k] = el;
													}
												}}
												message={t('userPanel.sessionsNew.reflection.autosaveMessage')}
												variant="success"
												autoHide
												autoHideDuration={3000}
												className="mb-2"
												tiny
											/>
											<StepSlider
												ref={ref}
												min={0}
												max={10}
												onChange={onChange}
												onChangeCommitted={() => autosave(k)}
												defaultValue={Number(v || 0)}
											/>
										</BoxSection>
									)}
								/>
							</Box>
						);
					case 'experienceUsage':
					case 'newKnowledge':
						return (
							<Box key={k} variant="white-bordered">
								<Controller
									name={k as any}
									control={control}
									render={({ field: { onChange, value } }) => (
										<BoxSection
											header={() => (
												<div className="flex flex-row gap-2 justify-content-start align-items-center">
													{title}
													{(v === undefined || value === undefined) && (
														<Icon name="circle" className="text-xs text-rose" />
													)}
												</div>
											)}
											headerClassName="pt-2 font-semibold heading-16-24 text-on-blue-grey"
										>
											<MessageBox
												ref={(el: MessageBoxRef) => {
													if (el) {
														autosaveMessageRefs.current[k] = el;
													}
												}}
												message={t('userPanel.sessionsNew.reflection.autosaveMessage')}
												variant="success"
												autoHide
												autoHideDuration={3000}
												className="mb-2"
												tiny
											/>
											<div className="flex flex-row gap-2 align-items-center mb-4">
												<Line className="flex-1" minWidth={20} />
												<SelectButtonGroupYesNo
													value={
														typeof value !== 'undefined' && value !== null ? value : null
													}
													defaultValue={v as any}
													onChange={({ value: newValue }: { value: any }) => {
														onChange(newValue);
														autosave(k);
													}}
												/>
											</div>
										</BoxSection>
									)}
								/>
							</Box>
						);
					default:
						return null;
				}
			})}

			<Button
				label={t('misc.forms.save')}
				className="align-self-end"
				loading={isCreateSubmitting || isUpdateSubmitting}
			/>
		</form>
	);
};

export default MentorEvaluationModule;
